import { api } from "./api";
import { emitDriverPlanningUpdated } from "@/utils/driverPlanningEvents";

export type DeliveryStatus = "PLANNED" | "IN_PROGRESS" | "DELIVERED" | "FAILED";

export interface Delivery {
  id: string;
  orderId: string;
  driverId: string;
  zoneId: string;
  deliveryDate: string; // Format YYYY-MM-DD
  departureTime?: string; // Format HH:mm
  scheduledDeliveryTime?: string; // Format HH:mm
  actualDeliveryTime?: string; // Format HH:mm
  status: DeliveryStatus;
  tourOrder: number;
  distanceKm?: number;
  /** Temps de trajet estimé en minutes (depuis l'étape précédente ou le dépôt). */
  estimatedTravelTimeMinutes?: number | null;
  notes?: string;
  /** Somme des commandes non réglées du client, commande en cours incluse (en euros). */
  customerOutstandingDue?: number;
  customerOutstandingOrders?: Array<{
    id: string;
    orderNumber: string;
    totalAmount: number;
    deliveryDate: string;
    isCurrent: boolean;
  }>;
  order?: {
    id: string;
    orderNumber: string;
    status?: string;
    customer: {
      id: string;
      firstName?: string | null;
      lastName: string;
      type?: "INDIVIDUAL" | "INSTITUTION" | "COMPANY";
      phone?: string;
      phoneDialCode?: string;
      /** Mode de paiement par défaut du client (EN_COMPTE = pas de saisie au livreur) */
      defaultPaymentMode?: "EN_COMPTE" | "COMPTANT" | null;
      zone?: { id: string; name: string };
      address?: string | null;
      city?: string | null;
    };
    customerAddress?: {
      id: string;
      address: string;
      postalCode?: string | null;
      city?: string | null;
      zone?: { id: string; name: string };
    } | null;
    deliveryTimeSlot?: string;
    paymentMethod?: {
      id: string;
      name: string;
      code?: string | null;
    } | null;
    paymentStatus?: {
      id: string;
      name: string;
      colorCode?: string | null;
    } | null;
    specialInstructions?: string;
    totalAmount: number;
    totalAmountHt?: number | null;
    vatAmount?: number | null;
    vatRate?: number | null;
    discountAmount?: number | null;
    deliveryCost?: number | null;
    serviceType?: string;
    items: Array<{
      id: string;
      quantity: number;
      unitPrice?: number;
      subtotal?: number;
      options?: string | null;
      notes?: string | null;
      product: { name: string };
    }>;
  };
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
  zone?: {
    id: string;
    name: string;
    zoneNumber: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeliveryData {
  orderId: string;
  driverId: string;
  zoneId: string;
  deliveryDate: string; // Format YYYY-MM-DD
  departureTime?: string; // Format HH:mm
  scheduledDeliveryTime?: string; // Format HH:mm
  tourOrder?: number;
  distanceKm?: number;
  estimatedTravelTimeMinutes?: number;
  notes?: string;
}

export interface UpdateDeliveryData {
  driverId?: string;
  zoneId?: string;
  deliveryDate?: string; // Format YYYY-MM-DD
  departureTime?: string; // Format HH:mm
  scheduledDeliveryTime?: string; // Format HH:mm
  actualDeliveryTime?: string; // Format HH:mm
  status?: DeliveryStatus;
  tourOrder?: number;
  distanceKm?: number;
  estimatedTravelTimeMinutes?: number | null;
  notes?: string;
}

export interface AssignDeliveryData {
  driverId: string;
  tourOrder?: number;
  scheduledDeliveryTime?: string; // Format HH:mm
}

export interface DeliveryFilter {
  driverId?: string;
  zoneId?: string;
  deliveryDate?: string; // Format YYYY-MM-DD
  deliveryDateFrom?: string; // Format YYYY-MM-DD
  deliveryDateTo?: string; // Format YYYY-MM-DD
  status?: DeliveryStatus;
}

/**
 * Service pour la gestion des livraisons
 */
export const deliveryService = {
  /**
   * Récupère toutes les livraisons avec filtres optionnels
   */
  async getAll(filters?: DeliveryFilter): Promise<Delivery[]> {
    const response = await api.get<{ success: boolean; data: Delivery[] }>(
      "/deliveries",
      {
        params: filters,
      },
    );
    return response.data.data;
  },

  /**
   * Récupère une livraison par son ID
   */
  async getById(id: string): Promise<Delivery> {
    const response = await api.get<{ success: boolean; data: Delivery }>(
      `/deliveries/${id}`,
    );
    return response.data.data;
  },

  /**
   * Récupère une livraison par l'ID de la commande
   */
  async getByOrderId(orderId: string): Promise<Delivery | null> {
    try {
      const response = await api.get<{ success: boolean; data: Delivery }>(
        `/deliveries/order/${orderId}`,
      );
      return response.data.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Crée une nouvelle livraison
   */
  async create(data: CreateDeliveryData): Promise<Delivery> {
    const response = await api.post<{ success: boolean; data: Delivery }>(
      "/deliveries",
      data,
    );
    emitDriverPlanningUpdated();
    return response.data.data;
  },

  /**
   * Met à jour une livraison
   */
  async update(id: string, data: UpdateDeliveryData): Promise<Delivery> {
    const response = await api.put<{ success: boolean; data: Delivery }>(
      `/deliveries/${id}`,
      data,
    );
    emitDriverPlanningUpdated();
    return response.data.data;
  },

  /**
   * Attribue une livraison à un livreur (pour drag & drop)
   */
  async assign(id: string, data: AssignDeliveryData): Promise<Delivery> {
    const response = await api.patch<{ success: boolean; data: Delivery }>(
      `/deliveries/${id}/assign`,
      data,
    );
    emitDriverPlanningUpdated();
    return response.data.data;
  },

  /**
   * Met à jour le statut d'une livraison
   * @param notes - Optionnel : commentaire ou données JSON (paymentMethod, failureCause, etc.)
   */
  async updateStatus(
    id: string,
    status: DeliveryStatus,
    actualDeliveryTime?: string,
    notes?: string,
  ): Promise<Delivery> {
    const response = await api.patch<{ success: boolean; data: Delivery }>(
      `/deliveries/${id}/status`,
      {
        status,
        actualDeliveryTime,
        notes,
      },
    );
    emitDriverPlanningUpdated();
    return response.data.data;
  },

  /**
   * Supprime une livraison
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/deliveries/${id}`);
    emitDriverPlanningUpdated();
  },

  /**
   * Optimise l'ordre des livraisons d'un livreur pour une date (Google Maps Directions).
   */
  async optimizeRoute(
    driverId: string,
    deliveryDate: string,
  ): Promise<Delivery[]> {
    const response = await api.post<{
      success: boolean;
      data: Delivery[];
      message?: string;
    }>("/deliveries/optimize-route", {
      driverId,
      deliveryDate,
    });
    emitDriverPlanningUpdated();
    return response.data.data;
  },

  /**
   * Répartition automatique : crée les livraisons depuis les commandes sans livraison,
   * répartit par zone (round-robin), puis numérote l'ordre de tournée par livreur.
   */
  async allocateAuto(deliveryDate: string): Promise<{
    created: number;
    assigned: number;
    byDriver: Record<string, number>;
    tourOrdersSet: number;
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        created: number;
        assigned: number;
        byDriver: Record<string, number>;
        tourOrdersSet: number;
      };
      message?: string;
    }>("/deliveries/allocate/auto", { deliveryDate });
    emitDriverPlanningUpdated();
    return response.data.data;
  },

  /**
   * Réorganise l'ordre de tournée (drag & drop). Réservé au livreur connecté.
   */
  async reorderMyDeliveries(
    updates: { deliveryId: string; tourOrder: number }[],
  ): Promise<void> {
    await api.post<{ success: boolean }>("/deliveries/reorder", { updates });
    emitDriverPlanningUpdated();
  },

  /**
   * Livreur démarre sa tournée : commandes planifiées → Prêtes, première livraison → En cours.
   */
  async confirmPrepared(deliveryDate: string): Promise<{ updated: number }> {
    const response = await api.post<{
      success: boolean;
      data: { updated: number };
      message?: string;
    }>("/deliveries/confirm-prepared", { deliveryDate });
    return response.data.data;
  },

  /**
   * Réaffectation en cas d'absence livreur : désassigne les livraisons du livreur pour la date,
   * puis répartit par zone (affectation par défaut).
   */
  async reassignAbsentDriver(
    driverId: string,
    deliveryDate: string,
  ): Promise<{ unassigned: number; reassigned: number; byDriver: Record<string, number> }> {
    const response = await api.post<{
      success: boolean;
      data: { unassigned: number; reassigned: number; byDriver: Record<string, number> };
      message?: string;
    }>("/deliveries/reassign-absent-driver", { driverId, deliveryDate });
    emitDriverPlanningUpdated();
    return response.data.data;
  },
};
